import {
  Alert,
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  Input,
  Link,
  Select,
  SelectItem,
  Chip,
  Spinner,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';

import { getSeasonalLoginImage } from '@/utils/seasonalImages';
import useHospitalFlags from '@/hooks/useHospitalFlags';
import useSegment from '@/hooks/useSegment';
import { useAccountFormMutation } from '@/patient-app-common/api/authApi';
import { useAppSelector } from '@/redux/hooks';
import { FormField } from '@/patient-app-common/types/Auth';
import { getURLParam } from '@/patient-app-common/utils';

import { AccountForm } from './AccountForm';
import styles from './styles/login.module.css';

const EVENT = 'Signup Start button clicked';

export function SignupAccess() {
  const location = useLocation();
  const { accessKey: urlAccessKey } = useParams<{ accessKey: string }>();
  const { route } = useAppSelector(({ app }) => app);
  const { i18n, t } = useTranslation('login');
  const { sendTrackEvent } = useSegment();
  const [accountForm, { isLoading }] = useAccountFormMutation();
  const { hospital } = useHospitalFlags();

  const [accessKey, setAccessKey] = useState(urlAccessKey || '');
  const [languageSelected, setLanguageSelected] = useState(true);
  const [languageField, setLanguageField] = useState<null | FormField>(null);
  const [form, setForm] = useState<FormField[]>([]);
  const [error, setError] = useState(false);

  const preselectLanguageField = (languages: FormField) => {
    const initLocale = getURLParam(location.search, 'lang');
    if (!initLocale) return languages;

    const preselectedLang = languages.options.find((lang: string[]) =>
      lang.includes(initLocale)
    );
    if (!preselectedLang) return languages;

    const langId = preselectedLang[1];
    return { ...languages, value: langId };
  };

  const getSignupForm = async (token: string) => {
    try {
      const results = await accountForm({ token }).unwrap();

      if (results.success) {
        const languages = _.find(results.form, ['fieldCode', 'language_id']);
        if (languages) {
          const langParam = getURLParam(location.search, 'lang');
          const preselected = preselectLanguageField(languages);
          setLanguageField(preselected);

          // Only skip language step if ?lang= was explicitly provided and matched
          if (langParam && preselected.value) {
            const filtered = results.form.filter(
              (field: FormField) =>
                !field.languageId ||
                field.languageId === Number(preselected.value)
            );
            _.remove(
              filtered,
              (field: FormField) => field.fieldCode === 'language_id'
            );
            const hiddenLang = { ...preselected, visible: false };
            filtered.push(hiddenLang);
            setForm(filtered);
            setLanguageSelected(true);
          } else {
            setForm(results.form);
            setLanguageSelected(false);
          }
        } else {
          setForm(results.form);
        }
      } else {
        setError(true);
      }
    } catch (e) {
      console.error(e);
      setError(true);
    }
  };

  // Auto-fetch form when access key is provided via URL (QR code flow)
  useEffect(() => {
    if (urlAccessKey && route !== null) {
      sendTrackEvent(EVENT, { selfSignup: true, qrCode: true });
      getSignupForm(urlAccessKey);
    }
  }, [route]);

  // Set locale from ?lang= query param
  useEffect(() => {
    if (urlAccessKey) {
      const initLocale = getURLParam(location.search, 'lang') || 'en';
      dayjs.locale(initLocale);
      i18n.changeLanguage(initLocale);
    }
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    sendTrackEvent(EVENT, { selfSignup: true });
    getSignupForm(accessKey);
  };

  const handleLocaleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (!languageField) return;

    const [, langId, locale] = e.target.value.split(',');
    const languageFieldCopy = { ...languageField };
    languageFieldCopy.value = langId;
    setLanguageField(languageFieldCopy);

    if (!locale) return;
    dayjs.locale(locale);
    i18n.changeLanguage(locale);
    document.documentElement.lang = locale;
  };

  const getFormWithSelectedLang = () => {
    const formCopy: FormField[] = [...form];

    if (!languageField?.value) return;

    _.remove(
      formCopy,
      (field) =>
        field.languageId && field.languageId !== Number(languageField.value)
    );
    _.remove(formCopy, (field) => field.fieldCode === 'language_id');

    const languageFieldCopy = { ...languageField };
    languageFieldCopy.visible = false;

    formCopy.push(languageFieldCopy);
    setForm(formCopy);
    setLanguageSelected(true);
  };

  const seasonalBackground = getSeasonalLoginImage();

  if (languageSelected && form.length > 0) {
    return (
      <div
        style={{ backgroundImage: `url(${seasonalBackground})` }}
        className={`${styles.background} min-h-screen`}
      >
        {urlAccessKey && (
          <div className="flex justify-center pt-4">
            <Chip
              variant="flat"
              color="primary"
              size="lg"
              startContent={
                <Icon icon="solar:key-bold" className="text-base" />
              }
            >
              {urlAccessKey}
            </Chip>
          </div>
        )}
        <AccountForm jsonForm={form} token={accessKey} />
        {urlAccessKey && <div className="pb-12" />}
      </div>
    );
  }

  return (
    <div
      style={{ backgroundImage: `url(${seasonalBackground})` }}
      className={styles.background}
    >
      <div className="flex flex-col items-center justify-center h-[100dvh]">
        <div className="sm:max-w-xl w-full">
          <Card shadow="sm" className="border border-grey px-4">
            <CardHeader className="flex flex-col">
              {hospital?.logo && (
                <img
                  className={styles.logoContainer}
                  src={hospital.logo}
                  alt={hospital.displayName}
                />
              )}
              <h1 className="text-xl font-semibold">
                {t('common.link.signup')}
              </h1>
            </CardHeader>
            <CardBody className="gap-4">
              {/* Loading state for QR code flow */}
              {urlAccessKey && !form.length && !error && (
                <div className="flex justify-center py-8">
                  <Spinner color="primary" size="lg" />
                </div>
              )}

              {/* Manual access key entry (no URL param or after QR error) */}
              {!form.length && (!urlAccessKey || error) && (
                <>
                  <p>{t('signupaccess.description')}</p>
                  <Alert
                    color="danger"
                    hideIcon
                    isVisible={error}
                    radius="none"
                    title={t('signupaccess.error')}
                  />
                  <Form onSubmit={handleSubmit}>
                    <Input
                      autoComplete="off"
                      color="primary"
                      className="mb-4"
                      isInvalid={error}
                      label={t('signupaccess.input.label')}
                      name="access"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setError(false);
                        setAccessKey(e.target.value);
                      }}
                      value={accessKey}
                      variant="bordered"
                    />
                    <Button
                      className="mx-auto w-1/2"
                      color="primary"
                      isLoading={isLoading}
                      type="submit"
                    >
                      {t('common.action.start')}
                    </Button>
                  </Form>
                </>
              )}

              {!_.isEmpty(languageField) && !languageSelected && (
                <>
                  <Select
                    className="max-w-xs place-self-center"
                    label={t('common.select.language')}
                    onChange={(e) => handleLocaleChange(e)}
                  >
                    {languageField.options.map(
                      (lang: [string, string, string]) => (
                        <SelectItem key={lang.toString()}>{lang[0]}</SelectItem>
                      )
                    )}
                  </Select>
                  <Button
                    className="mx-auto w-1/2"
                    color="primary"
                    onPress={getFormWithSelectedLang}
                  >
                    {t('common.action.next')}
                  </Button>
                </>
              )}
              <div className="flex flex-col items-center">
                <Link className="text-sm" href="/">
                  {t('common.link.back_to_login')}
                </Link>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
